import type { IHttpHeadersGetter, IRequestConfig, IWindowService } from 'angular';
import type { HttpResponse } from './HttpResponse';
import type { I18n } from '../i18n/mx-internationalization';
import type { ConfigService } from '../App/services/mx-config-service';

type ServerException = {
	ExceptionName?: string;
	Message: string;
	Key?: string;
	Arguments?: string[];
	Title?: string;
};

type AttributeError = Array<{
	key?: string;
	value?: string;
	Name?: string;
	Message?: string;
}>;

export type ApiErrorBody = ServerException | AttributeError | string;
export type HttpErrorResponse = HttpResponse<ApiErrorBody>;

export class ApiError extends Error implements HttpResponse<ApiErrorBody> {
	name = 'ApiError';
	title?: string;
	config: IRequestConfig;
	data: ApiErrorBody;
	headers: IHttpHeadersGetter;
	status: number;
	statusText: string;
	xhrStatus: 'complete' | 'error' | 'timeout' | 'abort';
	skipNotification = false;
	key = '';
	private readonly restHosts;

	constructor(
		private readonly response: HttpResponse<ApiErrorBody>,
		private readonly i18n: I18n,
		private readonly $window: IWindowService,
		private readonly configService: ConfigService
	) {
		super(response.message || '');
		this.title = response.title || '';
		this.config = response.config;
		this.data = response.data;
		this.headers = response.headers;
		this.status = response.status;
		this.statusText = response.statusText;
		this.xhrStatus = response.xhrStatus;
		this.skipNotification = Boolean(response.skipNotification);

		this.restHosts = configService.get('restHosts');
		this.prepareError();
	}

	private supportedServerExceptions = [
		'PandoraException',
		'PortalException',
		'PersistenceException',
		'ConnectorException',
		'SpsDataEngineException',
		'ComponentException',
		'SpsSecurityException',
		'RecordNotFoundException',
		'ServiceManagerException',
	].map((e) => e.toLowerCase());

	private isSupportedServerException(exceptionName?: string): boolean {
		return this.supportedServerExceptions.includes((exceptionName || '').toLowerCase());
	}

	prepareError() {
		if (this.message && this.title) {
			return;
		}

		if (this.data && typeof this.data === 'object' && !Array.isArray(this.data) && this.isSupportedServerException(this.data.ExceptionName)) {
			const errorData = this.data;
			if (errorData.Key) {
				this.message = errorData.Arguments && errorData.Arguments.length > 0 ? this.i18n.getFormatted(errorData.Key, ...errorData.Arguments) : this.i18n.get(errorData.Key);
				if (!this.message) {
					this.message = errorData.Message;
				}
			} else {
				this.message = errorData.Message;
			}

			this.title = errorData.Title || this.i18n.get('solutionBuilderAgent.services.httpErrorHandlerService.error');

			this.data = this.message;

			this.name = errorData.ExceptionName || 'Error';
			this.key = errorData.Key || '';
			return;
		}

		const configUrl = this.config?.url;
		const isApi = (): boolean =>
			typeof configUrl === 'string' && configUrl.length > 0 && Object.values(this.restHosts).some((host) => configUrl.toUpperCase().includes(host.toUpperCase()));

		const errorUrl: string = configUrl ? `${this.i18n.get('shell.errors.url')} ${configUrl}` : '';
		const errorStatus = `${this.statusText} (${this.status})`;

		switch (this.status) {
			case 0:
				this.message = errorUrl;
				this.title = this.i18n.get('shell.errors.service_unavailable');
				break;

			case -1: {
				this.message = this.i18n.get('shell.mx-shell-http.networkErrorMessage');
				this.title = this.i18n.get('shell.mx-shell-http.networkErrorTitle');
				console.log('Response', this.response);
				console.log('Response headers: ', this.response.headers());
				break;
			}
			case 403:
				this.message = this.i18n.get('shell.mx-shell-http.noPermissions');
				this.title = this.i18n.get('shell.mx-shell-http.noPermissionsTitle');
				break;

			case 404:
				if (isApi()) {
					this.message = errorUrl;
					this.title = errorStatus;
				}
				break;
			default:
				if (isApi()) {
					if (Array.isArray(this.data)) {
						let message = errorUrl;

						this.data.forEach((info) => {
							if (info.key) {
								message += `${info.key}: `;
							}
							if (info.value) {
								message += info.value.length < 500 ? info.value : info.value.substring(0, 150) + '...';
							}
							if (info.key || info.value) {
								message += '\n';
							}

							if (info.Name || info.Message) {
								message += `${info.Name ? info.Name + ': ' : ''}` + (info.Message ? info.Message : '') + '\n';
							}
						});
						this.message = message;
						this.title = errorStatus;
					} else if (typeof this.data === 'string') {
						this.message = `${errorUrl}\n ${this.data.split('\r\n').slice(0, 2).join('\n')}`;
						this.title = errorStatus;
					} else if (typeof this.data === 'object' && this.data.Message && !this.message) {
						this.message = this.data.Message;
						this.title = this.title || this.data.Title || this.i18n.get('solutionBuilderAgent.services.httpErrorHandlerService.error');
					}
				}
				break;
		}
	}
}
